import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useBackupPolling } from '../../hooks/useBackupPolling';
import { 
  Clock, 
  ChefHat, 
  Package,
  X,
  RefreshCw,
  Search,
  AlertCircle,
  Trash2,
  Volume2,
  VolumeX,
  Home
} from 'lucide-react';
import io from 'socket.io-client';
import './ChefPage.css';

const REFRESH_INTERVAL_SECONDS = 30;

const ChefPage = ({ goTo }) => {
  // ⚠️ CRITICAL: Check for businessId first
  const initialBusinessId = React.useMemo(() => {
    try {
      const userDataString = localStorage.getItem('user');
      if (!userDataString) {
        console.error('❌ No user data found in localStorage');
        return null;
      }
      const userData = JSON.parse(userDataString);
      return userData.businessId || userData.business_id || userData.business?.businessId || userData.business?.business_id || null;
    } catch (error) {
      console.error('❌ Error reading user data:', error);
      return null;
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productionQueue, setProductionQueue] = useState([]);
  const [zoneMetrics, setZoneMetrics] = useState({});
  const [selectedZone, setSelectedZone] = useState('ALL');
  const [searchFilter, setSearchFilter] = useState('');
  const [socket, setSocket] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showBatchSizeModal, setShowBatchSizeModal] = useState(false);
  const [selectedBatchItem, setSelectedBatchItem] = useState(null);
  const [customBatchSize, setCustomBatchSize] = useState('');
  const [categories, setCategories] = useState(['ALL']); // Categories from database
  const [tableFilter, setTableFilter] = useState(false); // Table filter toggle (true/false)
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    const saved = localStorage.getItem('chefVoiceEnabled');
    return saved !== null ? saved === 'true' : true; // Default enabled
  });
  const [chefDashboardEnabled, setChefDashboardEnabled] = useState(null); // null = not loaded yet
  
  // Use ref to always have current voice enabled state in callbacks
  const voiceEnabledRef = useRef(voiceEnabled);
  
  // Track previous batches to detect new items
  const previousBatchesRef = useRef([]);
  
  // Text-to-Speech utility - DEFINED FIRST before useEffects that use it
  const speakOrderItems = useCallback((items, forceEnabled = false) => {
    // Use ref to get current state, not stale closure value
    const shouldSpeak = forceEnabled || voiceEnabledRef.current;
    
    if (!shouldSpeak || !window.speechSynthesis) {
      return;
    }

    // Cancel any ongoing speech
    try {
      window.speechSynthesis.cancel();
    } catch (e) {
      console.warn('⚠️ Error cancelling speech:', e);
    }

    // Build announcement text
    let announcement = 'New order. ';
    
    if (items && items.length > 0) {
      items.forEach(item => {
        // Try multiple field name variations
        const itemName = item.menu_item_name || item.itemName || item.name || item.item_name || 'item';
        const quantity = item.quantity || item.current_quantity || item.qty || 1;
        const customizations = item.customizations || item.customization || [];
        
        announcement += `${quantity} ${itemName}. `;
        
        // ✅ FIX: Announce customizations if present
        if (customizations && Array.isArray(customizations) && customizations.length > 0) {
          announcement += `With customizations: ${customizations.join(', ')}. `;
        }
      });
    } else {
      announcement += 'Please check the screen.';
    }

    // Function to actually speak (after voices are loaded)
    const doSpeak = () => {
      try {
        // Create speech utterance
        const utterance = new SpeechSynthesisUtterance(announcement);
        utterance.rate = 0.85; // Slightly slower for sweet, clear voice
        utterance.pitch = 1.2; // Higher pitch for sweeter female voice
        utterance.volume = 1.0;
        utterance.lang = 'en-IN'; // Set language explicitly
        
        // Select female voice with preference for sweet-sounding voices
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
        // Priority 1: Look for female voices (common female voice names)
        const femaleVoice = voices.find(voice => 
          (voice.lang.includes('en-IN') || voice.lang.includes('en-GB') || voice.lang.includes('en-US')) &&
          (voice.name.toLowerCase().includes('female') || 
           voice.name.toLowerCase().includes('woman') ||
           voice.name.toLowerCase().includes('zira') || 
           voice.name.toLowerCase().includes('samantha') ||
           voice.name.toLowerCase().includes('victoria') ||
           voice.name.toLowerCase().includes('karen') ||
           voice.name.toLowerCase().includes('susan') ||
           voice.name.toLowerCase().includes('fiona') ||
           voice.name.toLowerCase().includes('catherine') ||
           voice.name.toLowerCase().includes('heera') ||
           voice.name.toLowerCase().includes('nicky') ||
           voice.name.toLowerCase().includes('raveena'))
        );
        
        // Priority 2: Any Indian female voice
        const indianFemale = !femaleVoice && voices.find(voice => 
          voice.lang.includes('en-IN') && 
          (voice.name.toLowerCase().includes('female') || !voice.name.toLowerCase().includes('male'))
        );
        
        // Priority 3: Any English female voice
        const englishFemale = !femaleVoice && !indianFemale && voices.find(voice => 
          (voice.lang.includes('en-GB') || voice.lang.includes('en-US')) &&
          (voice.name.toLowerCase().includes('female') || 
           voice.name.toLowerCase().includes('zira') ||
           voice.name.toLowerCase().includes('samantha'))
        );
        
        const selectedVoice = femaleVoice || indianFemale || englishFemale;
        
        if (selectedVoice) {
          utterance.voice = selectedVoice;
        }
      } else {
        console.warn('⚠️ No voices available yet, using default with high pitch');
      }

      utterance.onstart = () => {
      };

      utterance.onend = () => {
      };

      utterance.onerror = (event) => {
        console.error('❌ Speech error:', event.error, event);
        // Try to resume if suspended (common in some browsers)
        if (event.error === 'synthesis-failed' || event.error === 'synthesis-unavailable') {
          try {
            window.speechSynthesis.cancel();
            window.speechSynthesis.resume();
          } catch (e) {
            console.error('❌ Failed to resume:', e);
          }
        }
      };
      window.speechSynthesis.speak(utterance);
      } catch (error) {
        console.error('❌ Error in doSpeak:', error);
      }
    };

    // Check if voices are loaded
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      // Voices not loaded yet, wait for them
      // Set up voiceschanged listener
      const onVoicesChanged = () => {
        window.speechSynthesis.removeEventListener('voiceschanged', onVoicesChanged);
        doSpeak();
      };
      window.speechSynthesis.addEventListener('voiceschanged', onVoicesChanged);
      
      // Fallback timeout
      setTimeout(() => {
        window.speechSynthesis.removeEventListener('voiceschanged', onVoicesChanged);
        doSpeak();
      }, 1000);
    } else {
      // Voices already loaded, speak immediately
      doSpeak();
    }
  }, []); // No dependencies - uses ref
  
  // Update voice enabled ref when it changes
  useEffect(() => {
    voiceEnabledRef.current = voiceEnabled;
  }, [voiceEnabled]);

  // Initialize speech synthesis on first user interaction (browser requirement)
  useEffect(() => {
    const initializeSpeech = () => {
      if (window.speechSynthesis) {
        // Trigger speech synthesis to unlock it (browser security requirement)
        const utterance = new SpeechSynthesisUtterance('');
        utterance.volume = 0;
        window.speechSynthesis.speak(utterance);
      }
      // Remove listeners after first interaction
      document.removeEventListener('click', initializeSpeech);
      document.removeEventListener('touchstart', initializeSpeech);
    };
    
    // Add listeners for first user interaction
    document.addEventListener('click', initializeSpeech, { once: true });
    document.addEventListener('touchstart', initializeSpeech, { once: true });
    
    return () => {
      document.removeEventListener('click', initializeSpeech);
      document.removeEventListener('touchstart', initializeSpeech);
    };
  }, []);

  // Detect new batches and announce them (ONLY truly new batches)
  useEffect(() => {
    if (!voiceEnabled || !productionQueue || productionQueue.length === 0) {
      previousBatchesRef.current = productionQueue || [];
      return;
    }

    const previousBatches = previousBatchesRef.current;
    
    // On initial load, just store the batches WITHOUT announcing
    // This prevents announcing existing batches when page is opened/refreshed
    if (previousBatches.length === 0 && productionQueue.length > 0) {
      previousBatchesRef.current = productionQueue;
      return;
    }

    // Find NEW batches (batches that weren't in previous list)
    const newBatches = productionQueue.filter(batch => {
      return !previousBatches.some(prevBatch => 
        prevBatch.batch_id === batch.batch_id
      );
    });

    // Find EXISTING batches with increased quantity (new orders added to same batch)
    const increasedBatches = productionQueue
      .map(batch => {
        const prev = previousBatches.find(prevBatch => prevBatch.batch_id === batch.batch_id);
        const prevQty = Number(prev?.current_quantity || prev?.quantity || 0);
        const currentQty = Number(batch.current_quantity || batch.quantity || 0);
        const delta = currentQty - prevQty;
        return { batch, delta };
      })
      .filter(({ delta }) => delta > 0);

    if (newBatches.length > 0 || increasedBatches.length > 0) {
      
      // Convert batches to items format for voice announcement
      // ✅ FIX: Include customizations in the announcement
      const newBatchItems = newBatches.map(batch => ({
        menu_item_name: batch.menu_item_name,
        quantity: batch.current_quantity || batch.quantity || 1,
        customizations: batch.customizations || []
      }));

      const increasedBatchItems = increasedBatches.map(({ batch, delta }) => ({
        menu_item_name: batch.menu_item_name,
        quantity: delta,
        customizations: batch.customizations || []
      }));

      const itemsToAnnounce = [...newBatchItems, ...increasedBatchItems];

      speakOrderItems(itemsToAnnounce);
    }

    // Update previous batches
    previousBatchesRef.current = productionQueue;
  }, [productionQueue, voiceEnabled, speakOrderItems]);

  // Toggle voice feature
  const toggleVoice = useCallback(() => {
    setVoiceEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('chefVoiceEnabled', newValue.toString());
      
      // If disabling voice, cancel any ongoing speech
      if (!newValue) {
        try {
          window.speechSynthesis.cancel();
        } catch (e) {
          console.warn('⚠️ Error cancelling speech:', e);
        }
        return newValue;
      }
      
      // If enabling voice, play confirmation after a delay to ensure voices are loaded
      // This also serves as the required user gesture for speech synthesis (browser autoplay policy)
      setTimeout(() => {
        // Double-check voice is still enabled (in case of rapid clicking)
        if (voiceEnabledRef.current && window.speechSynthesis) {
          try {
            const voices = window.speechSynthesis.getVoices();
            
            const testUtterance = new SpeechSynthesisUtterance('Voice enabled');
            testUtterance.rate = 0.9;
            testUtterance.volume = 1.0;
            testUtterance.lang = 'en-IN';
            
            if (voices.length > 0) {
              const voice = voices.find(v => v.lang.includes('en-IN') || v.lang.includes('en-GB') || v.lang.includes('en-US'));
              if (voice) {
                testUtterance.voice = voice;
              }
            }
            
            // Add event handlers to track success/failure
            testUtterance.onerror = (e) => console.error('❌ Voice test error:', e);
            testUtterance.onend = () => {};
            
            window.speechSynthesis.speak(testUtterance);
          } catch (error) {
            console.error('❌ Error playing confirmation:', error);
          }
        }
      }, 300);
      
      return newValue;
    });
  }, []);

  const headerMeta = useMemo(() => {
    if (typeof window === 'undefined') {
      return {
        businessName: 'INVEXIS',
        menuText: 'Chef Dashboard'
      };
    }

    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const businessName = userData.businessName || userData.business_name || 'INVEXIS';
      return {
        businessName,
        menuText: 'Chef Dashboard'
      };
    } catch {
      return {
        businessName: 'INVEXIS',
        menuText: 'Chef Dashboard'
      };
    }
  }, []);

  // Get business ID from localStorage
  const getBusinessId = () => {
    try {
      const userDataString = localStorage.getItem('user');
      
      if (!userDataString) {
        console.error('❌ [getBusinessId] No user data in localStorage!');
        console.error('❌ Please log in to continue');
        console.error('📍 Available localStorage keys:', Object.keys(localStorage));
        return null;
      }
      
      const userData = JSON.parse(userDataString);
      
      // Check multiple possible locations for businessId
      const businessId = 
        userData.businessId || 
        userData.business_id || 
        userData.business?.businessId || 
        userData.business?.business_id;
      
      if (!businessId) {
        console.error('❌ [getBusinessId] businessId not found in user data!');
        console.error('❌ User object:', userData);
        console.error('❌ Checked: userData.businessId, userData.business_id, userData.business.businessId');
        return null;
      }
      
      return businessId;
    } catch (error) {
      console.error('❌ [getBusinessId] Error parsing user data:', error);
      return null;
    }
  };

  // Resolve API and Socket base URLs
  const API_BASE = (process.env.REACT_APP_API_URL).replace(/\/+$/, ''); // e.g., http://host:5000/api
  // Socket should point to server root (without /api). Allow explicit override via env
  const SOCKET_BASE = (process.env.REACT_APP_SOCKET_URL || API_BASE.replace(/\/api$/, '')).replace(/\/+$/, '');

  // Fetch dashboard toggle status
  const fetchDashboardStatus = useCallback(async () => {
    try {
      const businessId = getBusinessId();
      if (!businessId) {
        console.warn('⚠️ No businessId for dashboard status check');
        return;
      }

      const response = await fetch(`${API_BASE}/qr-billing/dashboard-toggles`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Business-ID': businessId
        }
      });

      if (response.ok) {
        const data = await response.json();
        const isEnabled = data.toggles?.chefEnabled !== false; // Default to true if not set
        setChefDashboardEnabled(isEnabled);
      } else {
        console.warn('⚠️ Failed to fetch dashboard status, defaulting to enabled');
        setChefDashboardEnabled(true);
      }
    } catch (error) {
      console.error('❌ Failed to fetch dashboard status:', error);
      setChefDashboardEnabled(true); // Default to enabled on error
    }
  }, [API_BASE]);

  // Fetch categories from backend
  const fetchCategories = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/chef/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.categories) {
          // Add "ALL" at the beginning
          const allCategories = ['ALL', ...data.categories.map(cat => cat.name)];
          setCategories(allCategories);
        }
      } else {
        console.warn('⚠️ Failed to fetch categories, using defaults');
        // Fallback to default categories
        setCategories(['ALL', 'Hot Kitchen', 'Wok/Sauté', 'Grill', 'Cold Prep', 'Fryer', 'Desserts', 'Beverages', 'Other']);
      }
    } catch (error) {
      console.error('❌ Error fetching categories:', error);
      // Fallback to default categories
      setCategories(['ALL', 'Hot Kitchen', 'Wok/Sauté', 'Grill', 'Cold Prep', 'Fryer', 'Desserts', 'Beverages', 'Other']);
    }
  }, [API_BASE]);

  // Fetch production queue from backend
  const fetchProductionQueue = useCallback(async () => {
    try {
      const businessId = getBusinessId();
      if (!businessId) {
        setError('Business ID not found. Please log in again.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams();
      if (selectedZone !== 'ALL') {
        params.append('zone', selectedZone);
      }
      if (searchFilter) {
        params.append('search', searchFilter);
      }

      const response = await fetch(
        `${API_BASE}/chef/production-queue?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'X-Business-ID': businessId
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch production queue: ${response.statusText}`);
      }

      const data = await response.json();
      
      setProductionQueue(data.batches || []);
      setZoneMetrics(data.zoneMetrics || {});
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching production queue:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedZone, searchFilter]);

  // Initialize WebSocket connection
  useEffect(() => {
    const businessId = getBusinessId();
    if (!businessId) {
      console.error('❌ No business ID found in localStorage');
      return;
    }

    // Fetch categories on mount
    fetchCategories();
    
    // Fetch dashboard toggle status
    fetchDashboardStatus();

    // Initialize speech synthesis voices (Chrome loads voices asynchronously)
    // This must be done in production/HTTPS context
    if (window.speechSynthesis) {
      // Preload voices
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length === 0) {
          return;
        }
      };
      
      // Initial load attempt
      loadVoices();
      
      // Listen for voiceschanged event (Chrome, Edge)
      window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    } else {
      console.warn('⚠️ Speech Synthesis API not available in this browser');
    }

    const newSocket = io(SOCKET_BASE, {
      auth: {
        token: localStorage.getItem('token')
      },
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      newSocket.emit('join_business', businessId);
    });

    newSocket.on('chef:registered', (data) => {
    });

    newSocket.on('new_order_portions', (data) => {
      // Visual alert for testing
      console.warn('🚨 NEW ORDER EVENT RECEIVED! 🚨');
      
      // ✅ FIX: Don't announce here - let the useEffect handle it after queue refresh
      // This prevents duplicate announcements (WebSocket + useEffect)
      fetchProductionQueue();
    });

    newSocket.on('update_batch_quantities', (data) => {
      fetchProductionQueue();
    });

    newSocket.on('portion_served', (data) => {
      fetchProductionQueue();
    });

    newSocket.on('batch_completed', (data) => {
      fetchProductionQueue();
    });

    newSocket.on('quick_serve_alert', (data) => {
      // Could show a toast notification here
    });

    newSocket.on('dashboard_toggle_changed', (data) => {
      if (data.type === 'chef') {
        setChefDashboardEnabled(data.enabled);
      }
    });

    newSocket.on('disconnect', (reason) => {
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ CHEF DASHBOARD - WEBSOCKET CONNECTION ERROR');
      console.error('🚨 Error:', error);
      console.error('📡 Trying to connect to:', SOCKET_BASE);
      console.error('🌐 Error message:', error.message);
      console.error('🔍 Error type:', error.type);
      console.error('📋 Full error:', error);
    });

    setSocket(newSocket);

    return () => {
      // Cleanup voice listener
      if (window.speechSynthesis) {
        window.speechSynthesis.removeEventListener('voiceschanged', () => {});
      }
      // Cleanup socket
      newSocket.close();
    };
  }, [fetchProductionQueue, fetchCategories, fetchDashboardStatus]);

  // Initial load
  useEffect(() => {
    fetchProductionQueue();
  }, [fetchProductionQueue]);

  // Backup to the WebSocket updates. A kitchen tablet is left powered on with
  // this screen in the foreground, so "is the tab hidden" never became true and
  // the poll ran into an empty kitchen all night, holding the Neon compute awake.
  // It now stops once the screen has gone untouched, and fires again the moment
  // someone taps it.
  useBackupPolling(() => fetchProductionQueue(), REFRESH_INTERVAL_SECONDS * 1000);

  // Dashboard toggle status: an owner turning the chef screen on or off is not
  // an urgent event, and the visibilitychange handler below re-checks the moment
  // the screen is looked at again.
  useBackupPolling(() => fetchDashboardStatus(), 60000);

  // Check dashboard status when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchDashboardStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchDashboardStatus]);

  // Apply dark theme to the whole page while in Chef POV
  useEffect(() => {
    try {
      document.body.classList.add('chef-pov-active');
    } catch (_) {}
    return () => {
      try {
        document.body.classList.remove('chef-pov-active');
      } catch (_) {}
    };
  }, []);

  // Calculate elapsed time since oldest portion
  const getElapsedTime = (oldestPortionTime) => {
    if (!oldestPortionTime) return '0m';
    
    // ✅ FIXED: Backend sends UTC timestamps, JS Date handles them correctly
    // No timezone conversion needed - timestamps are already in UTC
    const orderTime = new Date(oldestPortionTime);
    const now = new Date();
    const diffMs = now - orderTime;
    const diffMinutes = Math.floor(diffMs / 60000);
    
    // Handle negative time (clock skew or future timestamps)
    if (diffMinutes < 0) {
      console.warn('⚠️ Negative timer detected:', {
        raw: oldestPortionTime,
        parsed: orderTime.toISOString(),
        now: now.toISOString(),
        diffMinutes
      });
      return '0m';
    }
    
    // Format as "Xh Ym" for times >= 60 minutes
    if (diffMinutes < 60) {
      return `${diffMinutes}m`;
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const mins = diffMinutes % 60;
      return `${hours}h ${mins}m`;
    }
  };

  // Get time color based on elapsed time
  const getTimeColor = (oldestPortionTime) => {
    if (!oldestPortionTime) return 'time-normal';
    
    // ✅ FIXED: Use UTC timestamps directly without timezone conversion
    const orderTime = new Date(oldestPortionTime);
    const now = new Date();
    const diffMinutes = Math.floor((now - orderTime) / 60000);
    
    // Green for 0-30 minutes
    if (diffMinutes <= 30) return 'time-normal';
    // Red after 30 minutes
    return 'time-critical';
  };

  // Filter batches by zone and search, then sort by table or time
  const filteredBatches = useMemo(() => {
    return productionQueue
      .filter(batch => {
        const matchesZone = selectedZone === 'ALL' || batch.production_zone === selectedZone;
        const matchesSearch = !searchFilter || 
          batch.menu_item_name.toLowerCase().includes(searchFilter.toLowerCase());
        
        // ✅ NEW: Filter out items older than 24 hours (1440 minutes)
        let isWithin24Hours = true;
        if (batch.oldest_portion_timestamp) {
          const orderTime = new Date(batch.oldest_portion_timestamp);
          const now = new Date();
          const diffMinutes = Math.floor((now - orderTime) / 60000);
          isWithin24Hours = diffMinutes < 1440; // 24 hours = 1440 minutes
        }
        
        return matchesZone && matchesSearch && batch.current_quantity > 0 && isWithin24Hours;
      })
      .sort((a, b) => {
        // If table filter is active, sort by table number
        if (tableFilter) {
          const tableA = parseInt(a.table_number) || 999;
          const tableB = parseInt(b.table_number) || 999;
          if (tableA !== tableB) {
            return tableA - tableB; // Sort by table number ascending
          }
          // If same table, sort by most recent first
          const timeA = new Date(a.oldest_portion_timestamp).getTime();
          const timeB = new Date(b.oldest_portion_timestamp).getTime();
          return timeB - timeA;
        }
        // Otherwise, sort by oldest first (FIFO)
        const timeA = new Date(a.oldest_portion_timestamp).getTime();
        const timeB = new Date(b.oldest_portion_timestamp).getTime();
        return timeA - timeB; // Older orders (smaller timestamp) come first
      });
  }, [productionQueue, selectedZone, tableFilter, searchFilter]);

  // Handle start new batch click
  const handleStartBatch = (batch) => {
    setSelectedBatchItem(batch);
    setShowBatchSizeModal(true);
  };

  // Handle batch size selection
  const handleCreateBatch = (size) => {
    // TODO: Add API call to create batch
    setShowBatchSizeModal(false);
    setShowBatchModal(false);
    setCustomBatchSize('');
  };

  // Handle custom batch creation
  const handleCreateCustomBatch = () => {
    if (customBatchSize && parseInt(customBatchSize) > 0) {
      handleCreateBatch(parseInt(customBatchSize));
    }
  };

  if (loading && productionQueue.length === 0) {
    return (
      <div className="chef-page-container">
        <div className="loading-state">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '20px' }}>
            <div style={{ position: 'relative', width: '95px', height: '95px' }}>
              <img 
                src="/invexis-logo.png"
                alt="Invexis"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  padding: '0px',
                  pointerEvents: 'none',
                  objectFit: 'contain',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                  zIndex: 1
                }}
              />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '70px',
                height: '70px',
                border: '6px solid #000000',
                borderTop: '6px solid #f9c806',
                borderRight: '6px solid #f9c806',
                borderRadius: '50%',
                animation: 'chefDashboardSpin 1s linear infinite',
                zIndex: 16
              }}></div>
            </div>
            <p style={{ color: '#666', fontSize: '16px', margin: 0 }}>Loading production queue...</p>
          </div>
          <style>{`
            @keyframes chefDashboardSpin { 
              0% { transform: translate(-50%, -50%) rotate(0deg); } 
              100% { transform: translate(-50%, -50%) rotate(360deg); } 
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="chef-page-container">
      {/* Header */}
      <div className="chef-header">
        <div className="header-top">
          <div className="brand-group">
            <span className="brand-name">{headerMeta.businessName}</span>
            {/* Home Icon - Navigate to Dashboard (Right side) */}
            <button
              onClick={() => {
                // Store that we came from chef dashboard
                localStorage.setItem('previousDashboard', 'chef-dashboard');
                goTo && goTo('dashboard');
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '0px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                fontSize: '20px',
                marginLeft: 'auto'
              }}
              aria-label="Go to Dashboard"
              title="Go to Dashboard"
            >
              <Home size={22} />
            </button>
          </div>
        </div>
        <div className="header-divider" />
        <div className="header-main">
          <div className="header-title">
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', width: '100%'}}>
              <h1>{headerMeta.menuText}</h1>
              <div className="header-actions" style={{marginLeft: 'auto', display: 'flex', gap: '12px'}}>
                <button
                  className={`voice-toggle-btn ${voiceEnabled ? 'voice-on' : 'voice-off'}`}
                  onClick={toggleVoice}
                  aria-label={voiceEnabled ? 'Disable voice announcements' : 'Enable voice announcements'}
                  title={voiceEnabled ? 'Voice ON' : 'Voice OFF'}
                >
                  {voiceEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
                </button>
                <button
                  className="wastage-btn"
                  onClick={() => {
                    // Navigate to inventory overview with stock-out tab and Waste sub-tab
                    if (goTo) {
                      // Store the desired sub-tab in localStorage for StockOutForm
                      localStorage.setItem('stockOutInitialSubTab', 'Waste');
                      goTo('inventory-stock-out');
                    } else {
                      console.error('goTo function not available');
                    }
                  }}
                  aria-label="Open wastage overview"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
            {/* Show warning only when chef dashboard is disabled */}
            {chefDashboardEnabled === false && (
              <div className="chef-disabled-warning">
                switch on in owner dashboard to display the order
              </div>
            )}
          </div>
        </div>
      </div>

            {/* Error Display */}
      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Production Zone Filters */}
      <div className="zone-filters">
        {categories.map(zone => (
          <button
            key={zone}
            className={`zone-btn ${selectedZone === zone ? 'active' : ''}`}
            onClick={() => setSelectedZone(zone)}
          >
            {zone}
            {zone !== 'ALL' && zoneMetrics[zone] && (
              <span className="zone-metric">
                ~{Math.round(zoneMetrics[zone].average_cook_time_minutes || 0)}m
              </span>
            )}
          </button>
        ))}
        {/* Tables Toggle Button */}
        <button
          className={`zone-btn ${tableFilter ? 'active' : ''}`}
          onClick={() => setTableFilter(!tableFilter)}
        >
          Tables
        </button>
      </div>

      {/* Search Filter - Fresh Implementation */}
      <div className="chef-search-wrapper">
        <div className="chef-search-container">
          <div className="chef-search-icon">
            <Search size={18} />
          </div>
          <input
            type="text"
            placeholder="Search dishes..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="chef-search-input"
            autoComplete="off"
          />
          {searchFilter && (
            <button 
              onClick={() => setSearchFilter('')} 
              className="chef-search-clear"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
      {/* Production Cards Grid */}
      <div className="production-grid">
        {filteredBatches.length === 0 ? (
          <div className="empty-state">
            <ChefHat size={64} />
            <h2>No orders in queue</h2>
            <p>
              {searchFilter
                ? 'No dishes match your search'
                : selectedZone !== 'ALL'
                ? `No orders in ${selectedZone}`
                : 'All caught up!'}
            </p>
          </div>
        ) : (
          filteredBatches.map(batch => (
            <div key={batch.batch_id} className="production-card" style={{ position: 'relative' }}>
              {/* Table number badge at top right - only show when Tables filter is active */}
              {tableFilter && batch.table_number && (
                <div style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  padding: '4px 10px',
                  backgroundColor: '#fbbf24',
                  color: '#000000',
                  fontSize: '12px',
                  fontWeight: '600',
                  borderRadius: '6px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  zIndex: 1
                }}>
                  T{batch.table_number}
                </div>
              )}
              {/* Simplified item row: large dish name on the left with timer, yellow count pill on the right */}
              <div className="chef-dish-layout">
                <div className="chef-dish-name-section">
                  <h3 className="chef-dish-name">{batch.menu_item_name}</h3>
                  {/* Display customizations if present */}
                  {batch.customizations && Array.isArray(batch.customizations) && batch.customizations.length > 0 && (
                    <ul className="chef-customizations">
                      {batch.customizations.map((customization, idx) => (
                        <li key={idx} className="chef-customization-item">
                          {customization}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className={`chef-timer-display ${getTimeColor(batch.oldest_portion_timestamp)}`}>
                    <Clock size={14} />
                    <span>{getElapsedTime(batch.oldest_portion_timestamp)}</span>
                  </div>
                </div>
                <div className="chef-dish-quantity">x{batch.current_quantity}</div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Batch Management Modal */}
      {showBatchModal && (
        <div className="chef-batch-overlay" onClick={() => setShowBatchModal(false)}>
          <div className="chef-batch-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chef-batch-modal-header">
              <h2>Batch Management</h2>
              <button 
                className="chef-batch-close" 
                onClick={() => setShowBatchModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="chef-batch-modal-scroll">
              {filteredBatches.map(batch => (
                <div 
                  key={batch.batch_id} 
                  className={`chef-batch-item ${batch.current_quantity === 0 ? 'chef-accent-empty' : 'chef-accent-active'}`}
                >
                  <div className="chef-batch-item-head">
                    <div className="chef-batch-item-left">
                      <div className="chef-batch-item-title">{batch.menu_item_name}</div>
                      <div className="chef-batch-item-servings-row">
                        <span className="chef-batch-item-sub">Servings Remaining:</span>
                        <span className={`chef-batch-remaining ${batch.current_quantity === 0 ? 'chef-zero' : ''}`}>
                          {batch.current_quantity}
                        </span>
                      </div>
                      {batch.current_quantity > 0 ? (
                        <p className="chef-batch-item-meta">
                          Batch started {getElapsedTime(batch.consolidation_timer_started_at)}, {batch.current_quantity} servings remaining
                        </p>
                      ) : (
                        <p className="chef-batch-item-meta">No active batch</p>
                      )}
                    </div>
                  </div>
                  <div className="chef-batch-actions">
                    <button 
                      className="chef-batch-start-btn"
                      onClick={() => handleStartBatch(batch)}
                    >
                      ▶ Start New Batch
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Batch Size Selection Modal */}
      {showBatchSizeModal && selectedBatchItem && (
        <div className="chef-batch-size-overlay">
          <div className="chef-batch-size-modal">
            <div className="chef-batch-size-header">
              <h3>Start New Batch: {selectedBatchItem.menu_item_name}</h3>
              <p className="chef-batch-size-subtitle">Select batch size:</p>
            </div>
            
            <div className="chef-batch-size-options">
              <button 
                className="chef-batch-size-btn chef-large"
                onClick={() => handleCreateBatch(100)}
              >
                Large: 100 Servings
              </button>
              <button 
                className="chef-batch-size-btn chef-medium"
                onClick={() => handleCreateBatch(50)}
              >
                Medium: 50 Servings
              </button>
              <button 
                className="chef-batch-size-btn chef-small"
                onClick={() => handleCreateBatch(25)}
              >
                Small: 25 Servings
              </button>
              <button 
                className="chef-batch-size-btn chef-custom"
                onClick={() => {/* Expands custom input - already visible */}}
              >
                Custom Size
              </button>
            </div>

            <div className="chef-custom-batch-input">
              <label>Enter Custom Batch Size:</label>
              <div className="chef-custom-input-group">
                <input 
                  type="number" 
                  placeholder="Enter servings"
                  value={customBatchSize}
                  onChange={(e) => setCustomBatchSize(e.target.value)}
                />
                <button 
                  className="chef-create-custom-batch-btn"
                  onClick={handleCreateCustomBatch}
                  disabled={!customBatchSize || parseInt(customBatchSize) < 0}
                >
                  Create
                </button>
              </div>
            </div>

            <div className="chef-batch-size-footer">
              <button 
                className="chef-batch-cancel-btn"
                onClick={() => {
                  setShowBatchSizeModal(false);
                  setCustomBatchSize('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChefPage;